import { useMemo } from 'react';
import { Stack, Chip, Switch, Box, IconButton, Tooltip, Typography, FormControlLabel } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Edit, PauseCircle, Description, FolderOpen } from '@mui/icons-material';
import SerialNoDataGrid from '../ui/SerialNoDataGrid';
import type { DocumentConfigItem, DocumentGroup } from '../../services/partnerOnboardingService';
import type { OperationalZoneRow } from '../../services/zoneService';
import { entityLabel, groupHubsLabel, groupZonesLabel, groupIdFromRef, duplicateDocumentGroupIds,
  documentCountByGroup, documentCountLabel,
} from '../../utils/documentReviewUtils';
import { isLockedPartnerProfileGroup } from '../../constants/documentReviewFlow';
import GroupZoneChips from './GroupZoneChips';

function documentCountColor(stats?: { total: number; active: number }) {
  const total = stats?.total ?? 0;
  const active = stats?.active ?? 0;
  if (total === 0) return 'default' as const;
  if (active === total) return 'success' as const;
  if (active > 0) return 'warning' as const;
  return 'error' as const;
}

interface Props {
  groups: DocumentGroup[];
  zones?: OperationalZoneRow[];
  /** Full config list used for active/total document counts (not status-filtered). */
  countConfigs: DocumentConfigItem[];
  loading?: boolean;
  emptyMessage?: string;
  onManageDocs: (group: DocumentGroup) => void;
  onEditGroup: (group: DocumentGroup) => void;
  onToggleGroup: (id: string, isActive: boolean) => void;
  onDeactivateGroup: (group: DocumentGroup) => void;
}

export default function DocumentGroupsTable({
  groups, zones: zoneCatalog = [], countConfigs, loading, emptyMessage, onManageDocs, onEditGroup, onToggleGroup, onDeactivateGroup,
}: Props) {
  const docCountByGroup = useMemo(() => documentCountByGroup(countConfigs), [countConfigs]);

  const duplicateGroupIds = useMemo(() => duplicateDocumentGroupIds(groups), [groups]);

  const sortOnlyCol = {
    filterable: false,
    disableColumnMenu: true,
  } satisfies Partial<GridColDef>;

  const columns: GridColDef[] = [
    {
      ...sortOnlyCol,
      field: 'name',
      headerName: 'Group',
      flex: 1.4,
      minWidth: 260,
      valueGetter: (_v, row) => (row as DocumentGroup).name,
      renderCell: (p) => {
        const group = p.row as DocumentGroup;
        const isDuplicate = duplicateGroupIds.has(group._id);
        const zonesLabel = groupZonesLabel(group, zoneCatalog);
        const hubs = groupHubsLabel(group);
        return (
          <Tooltip
            title={`${group.name} · ${group.code} · ${entityLabel(group.entityType)} · ${zonesLabel} · ${hubs}`}
            placement="top-start"
          >
            <Box minWidth={0} py={0.5}>
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={700}>{group.name}</Typography>
                {isDuplicate && (
                  <Chip size="small" color="warning" variant="outlined" label="Same name" sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
                {isLockedPartnerProfileGroup(group) && (
                  <Chip size="small" color="info" variant="outlined" label="Default" sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, whiteSpace: 'normal' }}>
                {group.code}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }} alignItems="center">
                <Chip size="small" label={entityLabel(group.entityType)} sx={{ height: 22, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                <GroupZoneChips group={group} zones={zoneCatalog} />
                <Chip size="small" variant="outlined" label={hubs} sx={{ height: 22, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
              </Stack>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      ...sortOnlyCol,
      field: 'entityType',
      headerName: 'Partner type',
      flex: 0.8,
      minWidth: 150,
      valueGetter: (_v, row) => entityLabel((row as DocumentGroup).entityType),
      renderCell: (p) => (
        <Tooltip title={entityLabel((p.row as DocumentGroup).entityType)} placement="top-start">
          <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.4, py: 0.5 }}>
            {entityLabel((p.row as DocumentGroup).entityType)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      ...sortOnlyCol,
      field: 'docCount',
      headerName: 'Documents',
      width: 120,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      description: 'Active documents / total configured',
      valueGetter: (_v, row) => {
        const stats = docCountByGroup[(row as DocumentGroup)._id];
        return stats ? stats.active / Math.max(stats.total, 1) : 0;
      },
      renderCell: (p) => {
        const stats = docCountByGroup[p.row._id];
        const label = documentCountLabel(stats);
        return (
          <Tooltip title={stats?.total ? `${stats.active} active of ${stats.total} configured` : 'No documents configured'}>
            <Chip
              size="small"
              variant="outlined"
              label={label}
              color={documentCountColor(stats)}
              sx={{ fontWeight: 700, minWidth: 52 }}
            />
          </Tooltip>
        );
      },
    },
    {
      ...sortOnlyCol,
      field: 'isActive',
      headerName: 'Status',
      width: 130,
      sortable: false,
      renderCell: (p) => {
        const locked = isLockedPartnerProfileGroup(p.row as DocumentGroup);
        return (
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={!!p.row.isActive}
              disabled={locked}
              onChange={(e) => onToggleGroup(p.row._id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          label={(
            <Typography variant="caption" fontWeight={600}>
              {locked ? 'Always on' : p.row.isActive ? 'Active' : 'Inactive'}
            </Typography>
          )}
          sx={{ m: 0 }}
        />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => {
        const group = p.row as DocumentGroup;
        const locked = isLockedPartnerProfileGroup(group);
        return (
          <Stack direction="row" spacing={0.25} justifyContent="center">
            <Tooltip title="Manage documents">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onManageDocs(group)}
              >
                <Description fontSize="small" />
              </IconButton>
            </Tooltip>
            {!locked && (
              <Tooltip title="Edit group">
                <IconButton size="small" onClick={() => onEditGroup(group)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {locked ? null : group.isActive ? (
              <Tooltip title="Deactivate group">
                <IconButton size="small" color="warning" onClick={() => onDeactivateGroup(group)}>
                  <PauseCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="View documents">
                <IconButton size="small" onClick={() => onManageDocs(group)}>
                  <FolderOpen fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      <SerialNoDataGrid
        rows={groups}
        columns={columns}
        loading={loading}
        getRowId={(r) => r._id}
        autoHeight
        rowHeight={88}
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        sortingOrder={['asc', 'desc']}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          '& .MuiDataGrid-row.inactive-row': { opacity: 0.65 },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': { display: 'none' },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': { visibility: 'visible' },
        }}
        getRowClassName={(p) => (!(p.row as DocumentGroup).isActive ? 'inactive-row' : '')}
        slots={{
          noRowsOverlay: () => (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3, textAlign: 'center' }}>
              <Typography variant="body2" fontWeight={600}>{emptyMessage || 'No document groups found'}</Typography>
            </Stack>
          ),
        }}
      />
    </Box>
  );
}

